import { useEffect, useMemo, useState } from "react";
import { QuizWithProctoringOptions } from "../assessments-page-helper";
import apiClient from "../../../../helpers/api-client";
import { Box, Tooltip } from "@mui/material";
import { CoreTitleCard, coreToast } from "@verity/components/core";
import i18n from "../../../../i18n";
import ScanSection from "./ScanSection";
import ModalPage from "../../modal-page";
import styles from "./ScanSection.module.css";
import TakedownSection, { getQuestionText } from "./TakedownSection";
import { AssessmentQuestion } from "@verity/api-client/types";
import BackButton from "../../../../components/back-button";

type ContentProtectionPageProps = {
	onClose: () => void;
	assessment: QuizWithProctoringOptions;
	onUpdate: () => void; // Need this to update UI of the new scan date
};

export type ContentProtectionState =
	| "unstarted"
	| "scanInProgress"
	| "scanFailed"
	| "scanIncomplete"
	| "scanComplete"
	| "noQuestions";

export type FetchProgress = {
	total: number;
	completed: number;
};

export type DMCAQuestionSources = {
	url: string;
	email: string | null;
};

export type DMCAQuestion = Omit<AssessmentQuestion, "sources"> & {
	sources?: DMCAQuestionSources[];
	questionNumber?: number;
};

export default function ContentProtectionPage({ onClose, assessment, onUpdate }: ContentProtectionPageProps) {
	const [questions, setQuestions] = useState<DMCAQuestion[]>([]); // Represents all questions regardless if complete, incomplete or user created
	const [lastFetchedDate, setLastFetchedDate] = useState<string | null>(assessment.sourcesLastScanned);
	const [fetchProgress, setFetchProgress] = useState<FetchProgress>({ total: 0, completed: 0 });
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isError, setIsError] = useState<boolean>(false);
	const [showScanToast, setShowScanToast] = useState<boolean>(false);

	const retrievedQuestions: DMCAQuestion[] = useMemo(() => getCleanedQuestionsWithNumber(questions), [questions]);

	const pageState: ContentProtectionState = useMemo(() => {
		if (isError) return "scanFailed";
		if (isLoading) return "scanInProgress";
		if (!lastFetchedDate) return "unstarted";
		if (questions.length === 0) return "noQuestions";

		const numQuestionsFound = retrievedQuestions.length;

		// Check if the number of questions we're going to show = number of all LMS questions
		if (numQuestionsFound === getLMSQuestions(questions).length) {
			return "scanComplete";
		}
		return "scanIncomplete";
	}, [isError, isLoading, lastFetchedDate, questions, retrievedQuestions.length]);

	// Initial setup
	useEffect(() => {
		void (async () => {
			try {
				// If already scanned, get results from database
				if (assessment.sourcesLastScanned) {
					// Get all questions
					const allQuestions = await apiClient.assessment.getAssessmentQuestions(assessment.id);

					// Complete questions = has question text
					const completeQuestions = getQuestionsWithText(allQuestions);

					// Get emails for complete questions
					const questionsWithEmail = await Promise.all(
						completeQuestions.map(async (question) => await getQuestionSourcesWithEmail(question, false)),
					);
					setQuestions(removeDuplicateQuestions(questionsWithEmail, allQuestions));
					setIsLoading(false);
				}
			} catch (error) {
				console.error(error);
				setIsError(true);
			}
		})();
	}, [assessment]);

	// Show toast after scan button ->
	// toast depends on the page state and how many questions we were able to retrieve
	useEffect(() => {
		if (showScanToast) {
			showScanStatusToast(pageState, retrievedQuestions.length);
			setShowScanToast(false);
		}
	}, [showScanToast]);

	// Gets sources and emails for questions, updates UI progress state and returns questions with email. Does not add question numbers
	async function updateQuestionsWithSources(questions: AssessmentQuestion[]): Promise<DMCAQuestion[]> {
		if (questions.length === 0) return [];

		setFetchProgress({ total: questions.length, completed: 0 });

		const questionsWithEmail = await Promise.all(
			questions.map(async (q) => {
				const questionWithEmail = await getQuestionSourcesWithEmail(q, true);

				setFetchProgress((prev) => ({
					...prev,
					completed: prev.completed + 1,
				}));

				return questionWithEmail;
			}),
		);

		return questionsWithEmail;
	}

	// Gets LMS questions and sets questions with email. This is called when user the clicks the "(re)scan" button
	async function handleFetchAssessmentQuestions() {
		try {
			setIsLoading(true);
			setIsError(false);
			const lmsQuestions = await apiClient.assessment.getAssessmentQuestionsFromLMS(assessment.id);
			const completeLmsQuestions = getQuestionsWithText(lmsQuestions) as AssessmentQuestion[];

			// Always keep user questions (lmsProvidedQuestionId === null) exactly as they are
			const userQuestions = getUserQuestions(questions) as DMCAQuestion[];

			// Sync email/sources for good lms questions
			const questionsWithEmails = await updateQuestionsWithSources(completeLmsQuestions);

			const newQuestions = removeDuplicateQuestions([...questionsWithEmails, ...userQuestions], lmsQuestions);

			// update UI state
			setLastFetchedDate(new Date().toISOString());
			setQuestions(newQuestions);
			setShowScanToast(true);
		} catch (error) {
			console.error(error);
			setIsError(true);
		} finally {
			setIsLoading(false);
			onUpdate();
		}
	}

	async function handleUserQuestionUpload(userQuestions: string[]) {
		try {
			setIsLoading(true);
			setIsError(false);

			// Create user questions
			const createdUserQuestions = await Promise.all(
				userQuestions.map(async (q) => await apiClient.assessment.createUserQuestion(assessment.id, q)),
			);

			// Get email/sources for the user questions
			const userQuestionsWithEmail = await updateQuestionsWithSources(createdUserQuestions);

			// Get complete questions for the already existing questions
			const completeQuestions = getQuestionsWithText(questions) as DMCAQuestion[];

			// Combine both old and new questions. The backend checks for duplicate user questions.
			// Then combine with existing questions and remove duplcaites
			const newQuestions = removeDuplicateQuestions([...completeQuestions, ...userQuestionsWithEmail], questions);

			setQuestions(newQuestions);
			setShowScanToast(true);
		} catch (error) {
			console.error(error);
			setIsError(true);
		} finally {
			setIsLoading(false);
			onUpdate();
		}
	}

	// Does not set question number
	async function getQuestionSourcesWithEmail(
		question: AssessmentQuestion | DMCAQuestion,
		updateSources: boolean,
	): Promise<DMCAQuestion> {
		const questionSources = await apiClient.assessment.getQuestionSourcesWithEmail(
			assessment.id,
			question.id,
			updateSources,
		);

		return {
			...question,
			sources: questionSources.sources.map((sourceInfo) => ({
				url: sourceInfo.url,
				email: sourceInfo.email,
			})),
			numSources: questionSources.numSources,
		};
	}

	const takedownSection = (
		<TakedownSection pageState={pageState} questions={retrievedQuestions} lastFetchedDate={lastFetchedDate} />
	);

	return (
		<ModalPage>
			<Box className={styles.contentProtectionContainer}>
				<Box>
					<BackButton
						onBack={onClose}
						ariaLabel={i18n.t("portal.components.createAssessmentPageTSX.backButtonAriaLabel")}
					/>
				</Box>
				<CoreTitleCard
					title={i18n.t("portal.components.contentProtectionPageTSX.title")}
					description={i18n.t("portal.components.contentProtectionPageTSX.description")}
					slotProps={{
						title: {
							sx: {
								fontSize: "20px",
								fontWeight: 600,
								lineHeight: 1.4,
							},
						},

						description: {
							sx: {
								fontSize: "15px",
								fontWeight: 400,
								lineHeight: 1.6,
							},
						},
					}}
				/>
				<ScanSection
					pageState={pageState}
					onCancelScan={() => setIsLoading(false)}
					lastFetchedDate={lastFetchedDate}
					numQuestionsFound={retrievedQuestions.length}
					expectedNumQuestions={getLMSQuestions(questions).length}
					onFetchAssessmentQuestions={handleFetchAssessmentQuestions}
					onUserQuestionUploaded={handleUserQuestionUpload}
					fetchProgress={fetchProgress}
				/>
				{isTakedownSectionExpanded(pageState) ? (
					takedownSection
				) : (
					<Tooltip title={i18n.t("portal.components.contentProtectionPageTSX.review.beforeScanTooltip")}>
						<span>{takedownSection}</span>
					</Tooltip>
				)}
			</Box>
		</ModalPage>
	);
}

function showScanStatusToast(pageState: ContentProtectionState, numQuestionsFound: number) {
	coreToast.dismiss();
	if (pageState === "scanIncomplete") {
		coreToast.warning(i18n.t(`portal.components.contentProtectionPageTSX.scanAndCheck.scanIncomplete.toast`));
	} else if (pageState === "scanComplete") {
		coreToast.success(
			i18n.t(`portal.components.contentProtectionPageTSX.scanAndCheck.scanComplete.toast`, {
				count: numQuestionsFound,
			}),
		);
	}
}

export function isTakedownSectionExpanded(pageState: ContentProtectionState) {
	return pageState === "scanComplete" || pageState === "scanIncomplete";
}

/**
 * Given array of questions, returns questions that have either body text or title text.
 */
function getQuestionsWithText(questions: DMCAQuestion[] | AssessmentQuestion[]) {
	if (questions.length === 0) return [];
	const lmsQuestions = getLMSQuestions(questions);
	const userQuestions = getUserQuestions(questions);

	const completeLmsQuestions = lmsQuestions.filter(getQuestionText);
	return [...completeLmsQuestions, ...userQuestions];
}

function getUserQuestions(questions: DMCAQuestion[] | AssessmentQuestion[]) {
	return questions.filter((q) => q.lmsProvidedQuestionId === null);
}

function getLMSQuestions(questions: DMCAQuestion[] | AssessmentQuestion[]) {
	return questions.filter((q) => q.lmsProvidedQuestionId !== null);
}

/**
 * If there are user questions, the user question will replace one of the empty questions (incomplete question).
 * Also assigns question numbers
 * Returned array will have lms questions first, then user questions. questions will be sorted from smallest to greatest id.
 */
function getCleanedQuestionsWithNumber(questions: DMCAQuestion[]) {
	const lmsQuestions = getLMSQuestions(questions) as DMCAQuestion[];
	const userQuestions = getUserQuestions(questions) as DMCAQuestion[];

	let incompleteLmsCount = lmsQuestions.length - getQuestionsWithText(lmsQuestions).length;
	const finalQuestions: DMCAQuestion[] = [];

	// Keep LMS questions (replace incomplete ones with user questions if available)
	for (const q of lmsQuestions) {
		const questionHasText = !!getQuestionText(q);
		if (incompleteLmsCount > 0 && userQuestions.length > 0 && !questionHasText) {
			const replacement = userQuestions.shift()!;
			finalQuestions.push(replacement);
			incompleteLmsCount--;
		} else if (questionHasText) {
			finalQuestions.push(q);
		}
	}

	const sorted = finalQuestions.sort((a, b) => {
		const aIsLms = a.lmsProvidedQuestionId !== null;
		const bIsLms = b.lmsProvidedQuestionId !== null;

		if (aIsLms && !bIsLms) return -1;
		if (!aIsLms && bIsLms) return 1;

		return a.id - b.id;
	});

	// Assign question numbers
	return assignQuestionNumber(sorted);
}

function assignQuestionNumber(questions: DMCAQuestion[]): DMCAQuestion[] {
	return questions.map((q, i) => {
		return { ...q, questionNumber: i + 1 };
	});
}

/**
 * Given newly created/scanned questions and previous questions, returns one array with no duplicate questions.
 * If duplicate, keeps the new question.
 * Need this function because we need to store both incompete + complete questions
 * to know how many questions are expected for UI and incomplete workflow
 */
function removeDuplicateQuestions(
	newQuestions: DMCAQuestion[],
	oldQuestions: (AssessmentQuestion | DMCAQuestion)[],
): DMCAQuestion[] {
	const mergedMap = new Map<number, DMCAQuestion>();

	// Keep all new questions
	for (const q of newQuestions) {
		mergedMap.set(q.id, q);
	}

	// Merge remaining oldQuestions only if not already present
	for (const q of oldQuestions) {
		if (!mergedMap.has(q.id)) {
			const sources =
				Array.isArray(q.sources) && typeof q.sources[0] === "string"
					? (q.sources as string[]).map((url) => ({ url, email: null }))
					: (q.sources as DMCAQuestion["sources"]);

			mergedMap.set(q.id, {
				...q,
				sources,
				questionNumber: (q as DMCAQuestion).questionNumber,
			});
		}
	}

	return Array.from(mergedMap.values());
}
